import * as React from 'react'
import { documentService } from '@/services/DocumentService'
import logger, { toErrorMessage } from '@/helpers/logger'

interface UseDownloadDocumentResult {
  /** Fetch a fresh signed link and hand the file to the browser. */
  download: (id: string) => Promise<void>
  /** Ids whose links are still being prepared. Two downloads do not share one. */
  pendingIds: ReadonlySet<string>
  error: string | null
}

/**
 * Download a document.
 *
 * The link is fetched at click time rather than rendered into the page, because
 * these URLs are short-lived and carry their own authorization — putting one in
 * the DOM would leave a working credential sitting in the markup for as long as
 * the page stayed open.
 */
export function useDownloadDocument(): UseDownloadDocumentResult {
  const [pendingIds, setPendingIds] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const [error, setError] = React.useState<string | null>(null)

  const download = React.useCallback(async (id: string): Promise<void> => {
    setError(null)
    setPendingIds((current) => {
      const next = new Set(current)
      next.add(id)
      return next
    })
    try {
      const { downloadUrl } = await documentService.getDownloadUrl(id)
      // The response carries a Content-Disposition of `attachment`, so simply
      // navigating to it saves the file rather than replacing the page.
      const anchor = document.createElement('a')
      anchor.href = downloadUrl
      anchor.rel = 'noopener'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    } catch (caught) {
      logger.error('Download failed', { id, error: toErrorMessage(caught) })
      setError('That file could not be downloaded.')
    } finally {
      setPendingIds((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }, [])

  return { download, pendingIds, error }
}
